import React, { useContext } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { AIAssistantManager } from 'features/ai-assistant';
import {
  CandidateProfileV2,
  CompanyProfileV2,
  ContactProfileV2Route,
  HiringAuthorityProfileV2,
  JobOrderProfileV2
} from 'features/inventory-profiles';
import { ChatDrawer } from 'features/message-center';
import { NewCandidatePage } from 'features/new-candidate';
import { NewJobOrderPage } from 'features/new-job-order';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { UserProviderContext } from 'providers/UserProvider';
import { EntityRoutes } from 'routes/constants';
import PrivateRoute from 'routes/PrivateRoute';
import { Permissions, userHasPermission, userHasRoles } from 'services/Authorization';
import Notifier from 'UI/components/molecules/Notifier';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { Roles } from 'UI/constants/roles';
import BulkEmail from 'UI/pages/BulkEmail';
import CandidateProfile from 'UI/pages/CandidateProfile';
import Candidates from 'UI/pages/Candidates';
import Collections from 'UI/pages/Collections';
import { Activity } from 'UI/pages/CommandCenter/Activity';
import { CommissionsBoardPage } from 'UI/pages/CommandCenter/CommissionsBoard';
import { DailyPerformance } from 'UI/pages/CommandCenter/DailyPerformance';
import { DominateSheet } from 'UI/pages/CommandCenter/DominateSheet';
import { FallOffsBoardPage } from 'UI/pages/CommandCenter/FallOffsBoard';
import { OverallPerformance } from 'UI/pages/CommandCenter/OverallPerformance';
import { Overview } from 'UI/pages/CommandCenter/Overview';
import { ProductionBoardPage } from 'UI/pages/CommandCenter/ProductionBoard';
import { Recruiter } from 'UI/pages/CommandCenter/Recruiter';
import { TeamOverview } from 'UI/pages/CommandCenter/Team';
import Companies from 'UI/pages/Companies';
import CompanyProfile from 'UI/pages/CompanyProfile';
import ContactProfile from 'UI/pages/ContactProfile';
import Contacts from 'UI/pages/Contacts';
import { DailyPlan } from 'UI/pages/DailyPlan';
import DashboardActivity from 'UI/pages/Dashboard/Activity';
import DashboardBulkEmail from 'UI/pages/Dashboard/BulkEmail';
import FeeAgreementsBoard from 'UI/pages/Dashboard/FeeAgreements';
import DashboardOverview from 'UI/pages/Dashboard/Overview';
import DashboardPhone from 'UI/pages/Dashboard/Phone';
import SendoutBoard from 'UI/pages/Dashboard/Sendout';
import SendoutLeadersBoard from 'UI/pages/Dashboard/SendoutLeaders';
import UserToolReports from 'UI/pages/Dashboard/UserToolReports';
import EditCandidateDataSheet from 'UI/pages/EditCandidateDataSheet';
import EditJobOrder from 'UI/pages/EditDataSheet';
import ErrorPage from 'UI/pages/ErrorPage';
import FeeAgreements from 'UI/pages/FeeAgreements';
import HiringAuthorityProfile from 'UI/pages/HiringAuthorityProfile';
import Home from 'UI/pages/Home';
import JobOrderProfile from 'UI/pages/JobOrderProfile';
import JobOrders from 'UI/pages/JobOrders';
import Login from 'UI/pages/Login';
import Map from 'UI/pages/Map';
import { MessageCenter } from 'UI/pages/MessageCenter';
import NewCandidate from 'UI/pages/NewCandidates';
import { NewCandidatesFromResume } from 'UI/pages/NewCandidates/NewCandidatesFromResume';
import NewCompany from 'UI/pages/NewCompany';
import NewContact from 'UI/pages/NewContact';
import { NewJobOrder } from 'UI/pages/NewJobOrder';
import Pil from 'UI/pages/Pil';
import Placements from 'UI/pages/Placements';
import Preview from 'UI/pages/Preview';
import { Prospecting } from 'UI/pages/Prospecting';
import ResumeSearch from 'UI/pages/ResumeSearch';
import { Roleplay } from 'UI/pages/Roleplay';
import Roster from 'UI/pages/Roster';
import { SearchProjectFolders, SearchProjectMain } from 'UI/pages/SearchProject';
import SearchProjectPreview from 'UI/pages/SearchProjectPreview';
import Sendouts from 'UI/pages/Sendouts';
import SendoutsFromChannelPartner from 'UI/pages/SendoutsChannelPartner';
import Teams from 'UI/pages/Teams';
import { getFeatureFlags } from 'UI/utils';
import { isFeatureAccessible } from 'utils/featureAccesible';

const featureFlags = getFeatureFlags();

/**
 * Route entry of CompanyProfileV2: maps the route param to the V2 entry contract ({ id }).
 * Declared at module level so its type is stable: inside Routes it would remount the profile
 * on every render. Keyed by id so another company gets a fresh instance: a late response for the
 * previous company cannot overwrite the current one, and drawer/dialog state does not carry over.
 * @param {Object} props
 * @param {{ params: { id: string } }} props.match
 */
const CompanyProfileV2Route = ({ match }) => (
  <CompanyProfileV2 key={match.params.id} id={match.params.id} />
);

/**
 * Route entry of the Contact V2 gate: maps the route param to `{ id }`. Keyed by id so another
 * contact gets a fresh instance (conversion check, drawer and dialog state).
 * @param {Object} props
 * @param {{ params: { id: string } }} props.match
 */
const ContactProfileV2RouteEntry = ({ match }) => (
  <ContactProfileV2Route key={match.params.id} id={match.params.id} />
);

const HiringAuthorityProfileV2Route = ({ match }) => (
  <HiringAuthorityProfileV2 key={match.params.id} id={match.params.id} />
);

/**
 * Route entry of CandidateProfileV2: maps the route param to `{ id }`. Keyed by id so another
 * candidate gets a fresh instance.
 * @param {Object} props
 * @param {{ params: { id: string } }} props.match
 */
const CandidateProfileV2Route = ({ match }) => (
  <CandidateProfileV2 key={match.params.id} id={match.params.id} />
);

/**
 * Route entry of JobOrderProfileV2: maps the route param to `{ id }`. Keyed by id so another job
 * order (e.g. after Duplicate) gets a fresh instance.
 * @param {Object} props
 * @param {{ params: { id: string } }} props.match
 */
const JobOrderProfileV2Route = ({ match }) => (
  <JobOrderProfileV2 key={match.params.id} id={match.params.id} />
);

const Routes = () => {
  const [user] = useContext(UserProviderContext);
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();

  const SearchProjectsPage = checkIfFeatureFlagEnabled(FeatureFlags.SearchProjectsFolders)
    ? SearchProjectFolders
    : SearchProjectMain;

  const NewCandidateComponent = checkIfFeatureFlagEnabled(FeatureFlags.NewCandidateV2)
    ? NewCandidatePage
    : NewCandidate;

  const NewJobOrderComponent = checkIfFeatureFlagEnabled(FeatureFlags.NewJobOrderV2)
    ? NewJobOrderPage
    : NewJobOrder;

  const areNewProfilesEnabled = checkIfFeatureFlagEnabled(FeatureFlags.InventoryProfilesV2);

  const CandidateProfileComponent = areNewProfilesEnabled
    ? CandidateProfileV2Route
    : CandidateProfile;
  const CompanyProfileComponent = areNewProfilesEnabled ? CompanyProfileV2Route : CompanyProfile;
  const ContactProfileComponent = areNewProfilesEnabled
    ? ContactProfileV2RouteEntry
    : ContactProfile;
  const HiringAuthorityProfileComponent = areNewProfilesEnabled
    ? HiringAuthorityProfileV2Route
    : HiringAuthorityProfile;
  const JobOrderProfileComponent = areNewProfilesEnabled ? JobOrderProfileV2Route : JobOrderProfile;

  return (
    <>
      <Notifier />
      <ChatDrawer />
      <AIAssistantManager />

      <Switch>
        <Route
          exact
          path={EntityRoutes.Login}
          render={() => (user ? <Redirect to={EntityRoutes.Home} /> : <Login />)}
        />

        <Redirect exact from="/home" to={EntityRoutes.Home} />
        <PrivateRoute exact path="/" component={Home} />

        <PrivateRoute exact path={EntityRoutes.Map} component={Map} />

        <PrivateRoute exact path={EntityRoutes.Candidates} component={Candidates} />
        <PrivateRoute
          exact
          path={EntityRoutes.CandidateProfile}
          component={CandidateProfileComponent}
        />
        <PrivateRoute exact path={EntityRoutes.CandidateCreate} component={NewCandidateComponent} />
        <PrivateRoute
          exact
          path={EntityRoutes.CandidateCreateFromResume}
          component={NewCandidatesFromResume}
        />
        <PrivateRoute
          exact
          path={EntityRoutes.CandidateDataSheet}
          component={EditCandidateDataSheet}
        />

        <PrivateRoute exact path={EntityRoutes.Companies} component={Companies} />
        <PrivateRoute
          exact
          path={EntityRoutes.CompanyProfile}
          component={CompanyProfileComponent}
        />
        <PrivateRoute exact path={EntityRoutes.CompanyCreate} component={NewCompany} />

        <PrivateRoute exact path={EntityRoutes.JobOrders} component={JobOrders} />
        <PrivateRoute
          exact
          path={EntityRoutes.JobOrderProfile}
          component={JobOrderProfileComponent}
        />
        <PrivateRoute exact path={EntityRoutes.JobOrderCreate} component={NewJobOrderComponent} />
        <PrivateRoute exact path={EntityRoutes.JobOrderEdit} component={EditJobOrder} />

        <PrivateRoute exact path={EntityRoutes.DashboardOverview} component={DashboardOverview} />
        <PrivateRoute exact path={EntityRoutes.DashboardActivity} component={DashboardActivity} />
        <PrivateRoute exact path={EntityRoutes.DashboardPhone} component={DashboardPhone} />
        <PrivateRoute exact path={EntityRoutes.DashboardBulkEmail} component={DashboardBulkEmail} />

        <PrivateRoute exact path={EntityRoutes.Roster} component={Roster} />
        <PrivateRoute exact path={EntityRoutes.Industries} component={Pil} />
        <PrivateRoute exact path={EntityRoutes.Teams} component={Teams} />

        <PrivateRoute exact path={EntityRoutes.FeeAgreements} component={FeeAgreements} />

        <PrivateRoute exact path={EntityRoutes.Contacts} component={Contacts} />

        <PrivateRoute
          exact
          path={EntityRoutes.ContactProfile}
          component={ContactProfileComponent}
        />
        <PrivateRoute exact path={EntityRoutes.ContactCreate} component={NewContact} />
        <PrivateRoute
          exact
          path={EntityRoutes.HiringAuthorityProfile}
          component={HiringAuthorityProfileComponent}
        />

        <PrivateRoute exact path={EntityRoutes.SearchProject} component={SearchProjectsPage} />
        <PrivateRoute exact path={EntityRoutes.SearchProjects} component={SearchProjectsPage} />

        <PrivateRoute
          exact
          path={EntityRoutes.SearchProjectsPreview}
          component={SearchProjectPreview}
        />
        <PrivateRoute
          exact
          path={EntityRoutes.SearchProjectPreview}
          component={SearchProjectPreview}
        />

        <PrivateRoute exact path={EntityRoutes.BulkEmail} component={BulkEmail} />
        <PrivateRoute
          exact
          path={EntityRoutes.Sendouts}
          component={Sendouts}
          permissions={[Permissions.Sendouts.CanUseModule]}
        />

        <PrivateRoute exact path={EntityRoutes.DashboardSendout} component={SendoutBoard} />

        <PrivateRoute
          exact
          path={EntityRoutes.DashboardFeeAgreements}
          component={FeeAgreementsBoard}
        />

        <PrivateRoute
          exact
          path={EntityRoutes.DashboardToolsUsageReport}
          component={UserToolReports}
        />

        <PrivateRoute
          exact
          path={EntityRoutes.DashboardSendoutLeaders}
          component={SendoutLeadersBoard}
        />

        <PrivateRoute
          exact
          path={EntityRoutes.Placements}
          component={Placements}
          permissions={[Permissions.Placements.CanUseModule]}
        />

        <PrivateRoute
          exact
          path={EntityRoutes.SendoutsFromChannelPartner}
          component={SendoutsFromChannelPartner}
          permissions={[Permissions.Sendouts.CanUseModule]}
          enabled={featureFlags.includes(FeatureFlags.SendoutsChannelPartner)}
        />

        <PrivateRoute
          exact
          path={EntityRoutes.Collections}
          component={Collections}
          roles={[Roles.Coach, Roles.Finance, Roles.Leadership, Roles.AssistantRegionalDirector]}
        />

        <PrivateRoute
          exact
          path={EntityRoutes.CommandCenterDailyPerformance}
          component={DailyPerformance}
          enabled={
            userHasPermission(Permissions.CommandCenter.DailyPerformance.CanUseModule) ||
            userHasRoles([Roles.Recruiter])
          }
        />
        <PrivateRoute
          exact
          path={EntityRoutes.CommandCenterOverallPerformance}
          component={OverallPerformance}
          permissions={[Permissions.CommandCenter.Activity.CanUseModule]}
        />
        <PrivateRoute
          exact
          path={EntityRoutes.CommandCenterDominateSheet}
          component={DominateSheet}
          permissions={[Permissions.CommandCenter.DominateSheet.CanUseModule]}
        />
        <PrivateRoute
          exact
          path={EntityRoutes.CommandCenterOverview}
          component={Overview}
          permissions={[Permissions.CommandCenter.Overview.CanUseModule]}
        />
        <PrivateRoute
          exact
          path={EntityRoutes.CommandCenterActivity}
          permissions={[Permissions.CommandCenter.Activity.CanUseModule]}
          component={Activity}
        />
        <PrivateRoute
          path={EntityRoutes.CommandCenterOverviewTeam}
          permissions={[Permissions.CommandCenter.Activity.CanUseModule]}
          component={TeamOverview}
        />
        <PrivateRoute
          path={EntityRoutes.CommandCenterOverviewRecruiter}
          permissions={[Permissions.CommandCenter.Activity.CanUseModule]}
          component={Recruiter}
        />
        <PrivateRoute
          path={EntityRoutes.CommandCenterProductionBoard}
          enabled={featureFlags.includes(FeatureFlags.CommandCenterProductionBoardPage)}
          component={ProductionBoardPage}
          roles={[
            Roles.Coach,
            Roles.RegionalDirector,
            Roles.ProductionDirector,
            Roles.Operations,
            Roles.DataCoordinator,
            Roles.Finance,
            Roles.Staff,
            Roles.Leadership,
            Roles.AssistantRegionalDirector
          ]}
        />
        <PrivateRoute
          path={EntityRoutes.CommandCenterFallOffsBoard}
          enabled={featureFlags.includes(FeatureFlags.CommandCenterFallOffsBoard)}
          component={FallOffsBoardPage}
          roles={[
            Roles.Coach,
            Roles.RegionalDirector,
            Roles.ProductionDirector,
            Roles.Operations,
            Roles.DataCoordinator,
            Roles.Finance,
            Roles.Staff,
            Roles.Leadership,
            Roles.AssistantRegionalDirector
          ]}
        />
        <PrivateRoute
          path={EntityRoutes.CommandCenterCommissionsBoard}
          enabled={featureFlags.includes(FeatureFlags.CommandCenterCommissionsBoard)}
          component={CommissionsBoardPage}
        />

        <PrivateRoute
          exact
          path={EntityRoutes.MessageCenter}
          component={MessageCenter}
          enabled={featureFlags.includes(FeatureFlags.MessageCenter)}
          permissions={[Permissions.Textel.CanUseModule]}
        />

        <PrivateRoute
          exact
          path={EntityRoutes.DailyPlan}
          component={DailyPlan}
          enabled={featureFlags.includes(FeatureFlags.DailyPlan)}
          permissions={[Permissions.DailyPlan.CanUseModule]}
        />
        <PrivateRoute
          exact
          path={EntityRoutes.CandidatesSearch}
          component={ResumeSearch}
          enabled={isFeatureAccessible({
            checkIfFeatureFlagEnabled,
            featureFlag: FeatureFlags.ResumeSearch,
            permission: Permissions.ResumeSearch.CanUseModule,
            user
          })}
        />

        <PrivateRoute
          exact
          path={EntityRoutes.Roleplay}
          component={Roleplay}
          enabled={featureFlags.includes(FeatureFlags.AIRoleplay)}
          permissions={[Permissions.AIIntegrations.Roleplay]}
        />

        <PrivateRoute
          exact
          path={EntityRoutes.Sourcing}
          component={Prospecting}
          enabled={isFeatureAccessible({
            checkIfFeatureFlagEnabled,
            featureFlag: FeatureFlags.AIProspectingAssistant,
            permission: Permissions.AIIntegrations.ProspectingAssistant,
            user
          })}
        />

        <PrivateRoute path={EntityRoutes.Preview} component={Preview} />
        <PrivateRoute path="/404" component={() => <ErrorPage error={404} />} />
        <PrivateRoute path="*" component={() => <ErrorPage error={404} shouldGoBack />} />
      </Switch>
    </>
  );
};

export default Routes;
